import Big from 'big.js';
import axios from 'smg/axios';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as lineitems from 'smg/customer-api/lineitems';
import { HandleRequestError } from 'smg/errors';
import * as html from 'smg/html';
import * as models from 'smg/models';
import { DollarFormat } from 'smg/util';

document.querySelectorAll<HTMLElement>('.pricing-table').forEach(async $pricingTable => {
    if ($pricingTable.classList.contains('mold-fees')) {
        void generateChallengeCoinMoldFeeTable($pricingTable);
        return;
    }

    const lineItem = $pricingTable.getAttribute('data-line-item');
    const type = $pricingTable.getAttribute('data-type');
    const minQty = parseInt($pricingTable.getAttribute('data-min-qty') || '0', 10);
    const allowedSizes = new Set(($pricingTable.getAttribute('data-sizes') ?? '').split(';').filter(Boolean));

    if (!lineItem || !type) {
        return;
    }

    const $thead = $pricingTable.querySelector('thead') as HTMLTableSectionElement;
    const $tbody = $pricingTable.querySelector('tbody') as HTMLTableSectionElement;

    $thead.innerHTML = '';
    $tbody.innerHTML = '';

    const sizes = await lineitems.GetSizes(lineItem, type);

    const qtys = new Set<number>();
    for (const size of sizes) {
        if (allowedSizes.size && !allowedSizes.has(size.name)) {
            continue;
        }
        if (!size.prices.length) {
            continue;
        }

        for (const price of size.prices) {
            if (minQty && price.quantity < minQty) {
                continue;
            }

            qtys.add(price.quantity);
        }
    }

    const qtysSorted = Array.from(qtys).sort((a, b) => a - b);

    const pricesBySize = new Map<string, Map<number, Big>>();
    for (const size of sizes) {
        if (allowedSizes.size && !allowedSizes.has(size.name)) {
            continue;
        }

        const prices = new Map<number, Big>();
        for (const price of size.prices) {
            prices.set(price.quantity, price.price);
        }
        pricesBySize.set(size.name, prices);
    }

    const $tr = html.FromString('<tr><th>Size</th></tr>');
    $thead.appendChild($tr);

    for (const qty of qtys) {
        $tr.appendChild(html.FromString(`<th>${Intl.NumberFormat().format(qty)}</th>`));
    }

    for (const [size, prices] of pricesBySize) {
        if (!prices.size) {
            continue;
        }

        const $tr = html.FromString(`<tr><th scope="row">${html.Escape(size)}</th></tr>`);
        $tbody.appendChild($tr);

        for (const qty of qtysSorted) {
            const price = prices.get(qty);
            $tr.appendChild(html.FromString(`<td>${price ? DollarFormat(price) : ''}</td>`));
        }
    }
});

const $optionPricing = document.querySelectorAll<HTMLElement>('.child-line-item-option-price,.child-line-item-price');
$optionPricing.forEach(async $optionPricing => {
    const lineItem = $optionPricing.getAttribute('data-line-item');
    const child = $optionPricing.getAttribute('data-child');
    const option = $optionPricing.getAttribute('data-option');
    const free = $optionPricing.getAttribute('data-free');
    const template = $optionPricing.getAttribute('data-template');
    if (!lineItem || !(child || option)) {
        return;
    }

    let price: Big = Big(0);

    if (child && (!option || !$optionPricing.matches('.child-line-item-option-price'))) {
        price = await lineitems.GetChildPrice(lineItem, child);
    } else if (child && option) {
        price = await lineitems.GetOptionPrice(lineItem, child, option);
    }

    if ($optionPricing.hasAttribute('data-free') && Big(price).eq(0)) {
        $optionPricing.textContent = free;
        return;
    }

    if (template) {
        $optionPricing.textContent = template.replace('$', DollarFormat(price));
    } else {
        $optionPricing.textContent = DollarFormat(price);
    }

    $optionPricing.setAttribute('data-price', price.toString());
});

async function generateChallengeCoinMoldFeeTable($pricingTable: HTMLElement) {
    const lineItem = $pricingTable.getAttribute('data-line-item');
    const type = $pricingTable.getAttribute('data-type');
    const allowedSizes = new Set(($pricingTable.getAttribute('data-sizes') ?? '').split(';').filter(Boolean));

    const $tbody = $pricingTable.querySelector('tbody') as HTMLTableSectionElement;

    if (!lineItem || !type) {
        return;
    }

    const sizes = (await lineitems.GetSizes(lineItem, type)).filter(size => allowedSizes.has(size.name));

    $tbody.innerHTML = '';
    const rows = sizes.map(async size => {
        const $tr = html.FromString(`<tr><th scope="row">${html.Escape(size.name)}</th></tr>`);

        await getMoldFees(lineItem, type, size.id, size.width, size.height, 1).then(moldFees => {
            moldFees.forEach(moldFee => {
                const $td = html.FromString('<td></td>');
                $td.textContent = DollarFormat(moldFee);
                $tr.appendChild($td);
            });
        }).then(() => getMoldFees(lineItem, type, size.id, size.width, size.height, 301)).
            then(moldFees => {
                const $td = html.FromString('<td></td>');
                let text = 'FREE';
                if (moldFees[0].gt(0) || moldFees[1].gt(0)) {
                    text = '---';
                }
                $td.textContent = text;
                $tr.appendChild($td);
            });

        return $tr;
    });

    await Promise.all(rows).then($trs => {
        const sortedRows = $trs.sort((a, b) => {
            const aSize = Big((a.querySelector('th')?.textContent || '').replace(/[^0-9.]/g, ''));
            const bSize = Big((b.querySelector('th')?.textContent || '').replace(/[^0-9.]/g, ''));
            return aSize.minus(bSize).toNumber();
        });
        sortedRows.forEach($tr => $tbody.appendChild($tr));
    });
}

async function getMoldFees(product: string, lineItemTypeID: string, lineItemTypeSizeID: string, width: number, height: number, quantity: number): Promise<Big[]> {
    const baseMoldFee: Big[] = [Big(0), Big(0)];

    await axios.
        get<hyejoo.ChildLineItems>('/child-line-items', {
            params: {
                'q': `lineItem:"${product}" mold:yes`,
                'data': 'lineItemID,networkChildLineItem,childLineItemOptions,name,pluralName',
                'width': width,
                'height': height,
                'quantity': quantity,
                'lineItemTypeID': lineItemTypeID,
                'lineItemTypeSizeID': lineItemTypeSizeID,
            },
        }).
        then(resp => {
            for (const childLineItem of resp.data.childLineItems) {
                if (childLineItem.childLineItemOptions) {
                    for (const childLineItemOption of childLineItem.childLineItemOptions) {
                        if (childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_COIN_MOLD_FEE_NEW_ONE_SIDE) {
                            baseMoldFee[0] = Big(childLineItemOption.networkChildLineItemOption?.price || '0');
                        } else if (childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_COIN_MOLD_FEE_NEW_BOTH_SIDES) {
                            baseMoldFee[1] = Big(childLineItemOption.networkChildLineItemOption?.price || '0');
                        }
                    }
                }
            }
        }).
        catch(err => {
            HandleRequestError(err);
            throw err;
        });

    return baseMoldFee;
}
